import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Globe, ShieldCheck, ArrowLeft, CheckCircle2, Share2, RotateCcw, History, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminDomainActivation() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/domain-activation').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleItem = async (key, checked) => {
    setBusy(key);
    try {
      await api.put(`/admin/domain-activation/item/${key}`, { checked });
      load();
    } catch { toast.error('Update failed'); }
    setBusy('');
  };

  const resetChecklist = async () => {
    setBusy('reset');
    try {
      await api.post('/admin/domain-activation/reset');
      toast.success('Checklist reset');
      load();
    } catch { toast.error('Reset failed'); }
    setBusy('');
  };

  const activate = async () => {
    setBusy('activate');
    try {
      const r = await api.post('/admin/domain-activation/activate-social-tags', { confirmation: data.confirmation_text });
      toast.success(r.data.note || 'Social share tags activated');
      setDialogOpen(false);
      setConfirmChecked(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Activation failed'); }
    setBusy('');
  };

  const deactivate = async () => {
    setBusy('deactivate');
    try {
      const r = await api.post('/admin/domain-activation/deactivate-social-tags');
      toast.success(r.data.note || 'Back to prep-only');
      load();
    } catch { toast.error('Deactivation failed'); }
    setBusy('');
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="domain-activation-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the Domain Activation checklist.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const live = data.social?.live_social_enabled;
  const allChecked = data.checked_count === data.total;

  return (
    <div data-testid="admin-domain-activation">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><Globe className="h-6 w-6 text-[#C9A84C]" /> Domain Activation</h1>
          <p className="text-sm text-slate-500 mt-1">Private checklist for connecting and verifying <strong>{data.domain}</strong> before the public launch.</p>
        </div>
        <Button asChild variant="outline" className="h-11 font-bold btn-press shrink-0" data-testid="domain-back-launch-btn">
          <Link to="/admin/launch-content"><ArrowLeft className="h-4 w-4 mr-1.5" /> Launch Content</Link>
        </Button>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-5" data-testid="domain-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      {/* Checklist */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5" data-testid="domain-checklist">
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="font-serif font-bold text-[#1a2744] text-sm">Live domain verification checklist</p>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[10px] font-bold ${allChecked ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-amber-50 text-amber-900 border-amber-300'}`} data-testid="domain-progress-badge">
              {data.checked_count} / {data.total} verified
            </Badge>
            <Button size="sm" variant="outline" onClick={resetChecklist} disabled={busy === 'reset'} className="h-8 text-[11px] font-bold btn-press" data-testid="domain-reset-btn">
              <RotateCcw className="h-3 w-3 mr-1" /> Reset
            </Button>
          </div>
        </div>
        <div className="space-y-1.5">
          {data.items.map((it) => (
            <label key={it.key} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${it.checked ? 'bg-emerald-50/60 border-emerald-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`} data-testid={`domain-item-${it.key}`}>
              <Checkbox checked={it.checked} disabled={busy === it.key} onCheckedChange={(v) => toggleItem(it.key, !!v)} className="mt-0.5" data-testid={`domain-check-${it.key}`} />
              <span className="min-w-0">
                <span className={`text-sm font-semibold ${it.checked ? 'text-emerald-900' : 'text-slate-700'}`}>{it.label}</span>
                {it.checked && it.checked_at && <span className="block text-[10px] text-emerald-700/80 mt-0.5">Verified {String(it.checked_at).slice(0, 10)}</span>}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Social tag activation */}
      <div className={`rounded-xl border p-4 mb-5 ${live ? 'border-emerald-300 bg-emerald-50/50' : 'border-[#C9A84C]/50 bg-[#C9A84C]/5'}`} data-testid="domain-social-activation">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-1.5"><Share2 className="h-4 w-4 text-[#C9A84C]" /> Saved Social Share Tags</p>
            {live ? (
              <p className="text-xs text-emerald-800 mt-1" data-testid="domain-social-live-note">
                <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />
                Live for <strong>{data.social.final_domain}</strong> since {String(data.social.activated_at).slice(0, 10)}. Sharing itself is still fully manual — nothing auto-posts.
              </p>
            ) : (
              <p className="text-xs text-slate-600 mt-1" data-testid="domain-social-prep-note">
                Currently <strong>prep-only</strong>. Title, description, and share image are saved and ready. Do not activate until Steve confirms the domain is connected and approves.
              </p>
            )}
            <div className="mt-2 rounded-lg bg-white/70 border border-slate-200 p-2.5 text-[11px] text-slate-600 space-y-0.5">
              <p><span className="font-bold">Title:</span> {data.social.default_title || '—'}</p>
              <p><span className="font-bold">Description:</span> {data.social.default_description || '—'}</p>
              <p><span className="font-bold">Image:</span> {data.social.default_image || '—'}</p>
            </div>
          </div>
          <div className="shrink-0 flex flex-col gap-2">
            {!live ? (
              <Button onClick={() => { setConfirmChecked(false); setDialogOpen(true); }} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="domain-activate-btn">
                <Share2 className="h-4 w-4 mr-2 text-[#C9A84C]" /> Activate Saved Social Share Tags
              </Button>
            ) : (
              <Button variant="outline" onClick={deactivate} disabled={busy === 'deactivate'} className="h-11 font-bold btn-press" data-testid="domain-deactivate-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Switch Back to Prep-Only
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Activation log */}
      {(data.activation_log || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="domain-activation-log">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-1.5 mb-2"><History className="h-4 w-4 text-slate-400" /> Activation history</p>
          <div className="space-y-1">
            {[...data.activation_log].reverse().map((l, i) => (
              <p key={i} className="text-xs text-slate-600">
                <span className={`font-bold ${l.action === 'activated' ? 'text-emerald-700' : 'text-amber-700'}`}>{l.action === 'activated' ? 'Activated' : 'Deactivated'}</span>
                {' '}· {String(l.at).replace('T', ' ').slice(0, 16)} UTC{l.by ? ` · ${l.by}` : ''}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="domain-activation-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#1a2744] flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Confirm activation</DialogTitle>
            <DialogDescription>
              This marks the saved social share tags as live for {data.domain}. It does not post anything anywhere — sharing stays fully manual. This can be reversed.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer" data-testid="domain-confirm-label">
            <Checkbox checked={confirmChecked} onCheckedChange={(v) => setConfirmChecked(!!v)} className="mt-0.5" data-testid="domain-confirm-checkbox" />
            <span className="text-sm text-slate-700 font-semibold">{data.confirmation_text}</span>
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-10 font-bold" data-testid="domain-cancel-btn">Cancel</Button>
            <Button onClick={activate} disabled={!confirmChecked || busy === 'activate'} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="domain-confirm-activate-btn">
              {busy === 'activate' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2 text-[#C9A84C]" />} Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
